import { AssignedTag } from "./assigned-tag.type";
import type { Mention, MentionedUser } from "./mentions.type";
import { MinimalStaffMember, StaffMember } from "./staff.type";

export interface StaffTaskAttachment {
  id: number;
  file: string;
  original_filename: string;
  created_at: string;
  created_by: MinimalStaffMember | null;
  updated_by: MinimalStaffMember | null;
  // Server-computed: only the uploader may remove a file.
  can_delete?: boolean;
}

export interface StaffTaskCompletionAudit {
  completed_at: string | null;
  completed_by: (Partial<StaffMember> & { full_name?: string }) | null;
  source: "easy_audit" | "task_status" | "staff_task";
  is_reliable: boolean;
}

// One row of GET /staff-tasks/{id}/history/. The backend composes the display
// strings (action verbs, "old → new" changes with labels resolved) — the
// frontend renders them verbatim and does no diff formatting.
export interface StaffTaskHistoryEvent {
  id: number;
  user: (Partial<StaffMember> & { full_name?: string }) | null;
  event_type: number;
  datetime: string;
  entity_type: "Task" | "Status" | "Collaborator";
  action: string;
  summary: string | null;
  changes: string | null;
}

export interface StaffTask {
  id: number;
  title: string;
  priority: string | number | null;
  description: string;
  status?: number;
  created_by?: Partial<StaffMember>;
  assignee: Partial<StaffMember>;
  assignees: {
    created_by: MinimalStaffMember | null;
    id: number;
    task: number;
    updated_by: MinimalStaffMember | null;
    user: Partial<StaffMember>;
  }[];
  comments?: TaskComment[];
  comment_count?: number;
  due_at?: string;
  completed_at?: string | null;
  created_at: string;
  updated_at?: string;
  patient?: any;
  assigned_tags?: AssignedTag[];
  attachments?: StaffTaskAttachment[];
  implementation?: number | null;
  implementation_phase?: number | null;
  color?: string | null;
  // Backed by an Exists annotation (enablement.ScenarioRun -> this task) — false, not missing,
  // whenever DEFAULT_STAFF_TASKS_QUERY requests it. See enablement/CLAUDE.md.
  is_training?: boolean;
  /**
   * People @mentioned in the description (TEAM_MENTIONS_FEATURE), notified when the task was saved.
   * Only present when the RestQL query names `mentions` (flag on); an older backend 400s the key.
   */
  mentions?: StaffTaskMention[];
  /**
   * The team the task was sent to (TEAMS_FEATURE). With `team` set and no `assignee` the task is
   * UNCLAIMED. Only present when the RestQL query names `team`; an older backend 400s the key.
   */
  team?: StaffTaskTeam | null;
  /** When the current assignee took it from the team; null while unclaimed. */
  team_claimed_at?: string | null;
}

export interface StaffTaskTeam {
  id: number;
  name: string;
  color: string;
  is_active?: boolean;
  alert_mode?: "screen" | "bell" | "bell_phone";
}

/** Sent to a team and nobody has taken it yet. */
export const isUnclaimedTeamTask = (
  task: Pick<StaffTask, "team" | "assignee"> | undefined
): boolean => Boolean(task?.team && !task.assignee?.id);

export type StaffTaskMention = Mention;

export interface TaskComment {
  created_by: MinimalStaffMember;
  created_at: string;
  id: number;
  text: string;
  updated_by: MinimalStaffMember | null;
  user: MinimalStaffMember;
  /** People @mentioned in this comment. Only when the query names it (TEAM_MENTIONS_FEATURE). */
  mentioned_users?: MentionedUser[];
}

export interface TaskCommentResponse {
  created_by: number;
  id: number;
  text: string;
  updated_by: number;
  user: number;
  created_at: string;
}

export interface StaffTaskInput {
  id?: number;
  title?: string;
  status?: string | number;
  priority?: string | number | null;
  description?: string;
  assignee?: number | null;
  assignees?: { user: number }[];
  due_at?: string | null;
  completed_at?: string | null;
  patient?: number | null;
  color?: string | null;
  /** TEAMS_FEATURE: send the task to a team (with `assignee: null` it is unclaimed). */
  team?: number | null;
}

export interface StaffTasksFilter {
  query?: string;
  search?: string;
  is_active?: boolean;
  is_complete?: boolean;
  patient?: number;
  assignee?: number;
  created_by?: number;
  after_due_at?: string;
  before_due_at?: string;
  is_order_by_due_at?: boolean;
  status?: number[];
  priority?: number[];
  limit?: number;
  offset?: number;
  group_by_priority?: boolean;
  ordering?: string;
  created_from_bulk_action?: boolean;
  implementation?: number;
  has_implementation?: boolean;
  /** Tasks this user is @mentioned on, in the description or a comment (TEAM_MENTIONS_FEATURE). */
  mentioned_user?: number;
  /** TEAMS_FEATURE (Home → For me): tasks sent to this team / to any of my teams. */
  team?: number;
  my_teams?: boolean;
  /** true: sent to a team and nobody has taken it; false: taken or never a team task. */
  unclaimed?: boolean;
  /** Leave out tasks this person owns ("taken by a teammate"). */
  exclude_assignee?: number;
}

export type StaffTasksResponse = StaffTask[];

export type StaffTasksPaginationResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: StaffTask[];
};

export type StaffTasksGroupByPriorityItem = {
  priority: number;
  count: number;
  tasks: StaffTask[];
};

export type StaffTasksGroupByPriorityResponse = {
  totalCount: number;
  items: Record<number | string, StaffTasksGroupByPriorityItem>;
};

export type StaffTasksDownloadQuery = StaffTasksFilter & {
  csv_header?: string;
  csv_labels?: string;
  title?: string;
  current_clinic?: number;
};

export interface CreateBulkTaskInput {
  priority?: string | number | null;
  assignee?: number | null;
  additional_assignees?: { user: number }[];
  title: string;
  description?: string;
  due_at?: string | null;
  patients_filter?: Record<string, any>;
  patient_products_filter?: Record<string, any>;
  appointments_filter?: Record<string, any>;
}

export type UpdateBulkTaskRequest = {
  task_ids?: number[];
  assignee?: number[];
  created_by?: number[];
  patient?: number[];
  status?: number[];
  priority?: number[];
  is_active?: boolean;
  is_complete?: boolean;
  after_due_at?: string;
  before_due_at?: string;
  created_from_bulk_action?: boolean;
  update_priority?: number;
  update_assignee?: number;
  update_status?: number;
  update_due_at?: string;
  update_assignees?: number[];
};

export interface StaffTaskBulkReassignInput {
  from_user_id: number;
  to_user_id: number;
  include_completed?: boolean;
  include_inactive?: boolean;
  include_overdue?: boolean;
}
